import { FormEvent, useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Input from '../components/common/Input';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { rbacService } from '../services/rbacService';
import {
  CreateManagedUserPayload,
  ManagedUser,
  userManagementService,
} from '../services/userManagementService';
import { warehouseService } from '../services/warehouseService';
import { ApiError } from '../services/api';
import { INPUT_SANITIZERS, FIELD_VALIDATORS } from '../utils/validationHelpers';

type UserStatus = 'active' | 'inactive';

const INITIAL_CREATE_FORM: CreateManagedUserPayload = {
  email: '',
  warehouseId: 0,
  roleId: undefined,
  name: '',
  phone: '',
  status: 'active',
};

export default function UserManagementPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [search, setSearch] = useState('');
  const [draftSearch, setDraftSearch] = useState('');
  const [sort, setSort] = useState<'createdAt' | 'email' | 'name' | 'status'>('createdAt');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [roleFilter, setRoleFilter] = useState<number | undefined>(undefined);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [createForm, setCreateForm] = useState<CreateManagedUserPayload>(INITIAL_CREATE_FORM);
  const [editForm, setEditForm] = useState({
    warehouseId: undefined as number | undefined,
    roleId: undefined as number | undefined,
    name: '',
    phone: '',
    status: 'active' as UserStatus,
  });
  const [showFilters, setShowFilters] = useState(false);

  // Add validation error states (Client Onboarding pattern)
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  // Hide body scrollbar on mount, restore on unmount
  useEffect(() => {
    document.body.classList.add('scrollbar-hide');
    document.documentElement.classList.add('scrollbar-hide');

    return () => {
      document.body.classList.remove('scrollbar-hide');
      document.documentElement.classList.remove('scrollbar-hide');
    };
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(draftSearch);
    }, 500);

    return () => clearTimeout(timer);
  }, [draftSearch]);

  const { data: usersResponse, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['user-management', { search, sort, order, roleId: roleFilter }],
    queryFn: ({ pageParam = 1 }) =>
      userManagementService.getUsers({
        search: search || undefined,
        roleId: roleFilter,
        sort,
        order,
        page: pageParam,
        limit: 20,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage?.meta) return undefined;
      const { page, totalPages } = lastPage.meta;
      return page < totalPages ? page + 1 : undefined;
    },
  });

  const { data: roles = [] } = useQuery({
    queryKey: ['rbac', 'roles'],
    queryFn: rbacService.getRoles,
  });

  const { data: warehousesResponse } = useQuery({
    queryKey: ['user-management', 'warehouses'],
    queryFn: () => warehouseService.getWarehouses({ page: 1, limit: 100, status: 'active' }),
  });

  const users = usersResponse?.pages?.flatMap(page => page.items) ?? [];
  const warehouses = warehousesResponse?.items ?? [];
  const totalUsers = usersResponse?.pages?.[0]?.meta?.total ?? 0;

  const roleOptions = useMemo(
    () =>
      roles.map((role) => ({
        value: role.id,
        label: role.name,
      })),
    [roles],
  );

  // Input change handlers with validation (Client Onboarding pattern)
  const handleCreateInputChange = (field: keyof CreateManagedUserPayload, value: any) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (createErrors[field]) {
      setCreateErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleEditInputChange = (field: string, value: any) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (editErrors[field]) {
      setEditErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Specialized input handlers (Client Onboarding pattern)
  const handleAlphabetsOnly = <T extends string>(handler: (field: T, value: any) => void, field: T) => 
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = INPUT_SANITIZERS.alphabetsOnly(e.target.value);
      handler(field, sanitized);
    };

  const handleMobileChange = <T extends string>(handler: (field: T, value: any) => void, field: T) => 
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = INPUT_SANITIZERS.digitsOnly(e.target.value);
      if (sanitized.length <= 10) {
        handler(field, sanitized);
      }
    };

  const handleEmailChange = <T extends string>(handler: (field: T, value: any) => void, field: T) => 
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = INPUT_SANITIZERS.emailFormat(e.target.value);
      handler(field, sanitized);
    };

  // Validation functions (Client Onboarding pattern)
  const validateCreateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!createForm.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (createForm.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(createForm.name.trim())) {
      newErrors.name = 'Name can only contain letters and spaces';
    }

    // Email validation
    const emailError = FIELD_VALIDATORS.email(createForm.email, true);
    if (emailError) newErrors.email = emailError;

    // Phone validation
    const phoneError = FIELD_VALIDATORS.mobileNumber(createForm.phone, true);
    if (phoneError) newErrors.phone = phoneError;

    // Warehouse validation
    if (!createForm.warehouseId) {
      newErrors.warehouseId = 'Warehouse is required';
    }

    // Role validation
    if (!createForm.roleId) {
      newErrors.roleId = 'Role is required';
    }

    setCreateErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEditForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!editForm.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (editForm.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(editForm.name.trim())) {
      newErrors.name = 'Name can only contain letters and spaces';
    }

    // Phone validation
    const phoneError = FIELD_VALIDATORS.mobileNumber(editForm.phone, true);
    if (phoneError) newErrors.phone = phoneError;

    // Warehouse validation
    if (!editForm.warehouseId || editForm.warehouseId === 0) {
      newErrors.warehouseId = 'Warehouse is required';
    }

    // Role validation
    if (!editForm.roleId) {
      newErrors.roleId = 'Role is required';
    }

    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createUserMutation = useMutation({
    mutationFn: userManagementService.createUser,
    onSuccess: () => {
      showToast('User created successfully', 'success');
      setShowCreateModal(false);
      setCreateForm(INITIAL_CREATE_FORM);
      clearCreateErrors(); // Clear validation errors
      queryClient.invalidateQueries({ queryKey: ['user-management'] });
    },
    onError: (error: ApiError) => {
      if (error.errors && error.errors.length > 0) {
        // Show all validation errors
        error.errors.forEach(err => showToast(err, 'error'));
      } else {
        // Show main error message
        showToast(error.message || 'Failed to create user', 'error');
      }
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: number;
      payload: {
        warehouseId?: number;
        roleId?: number;
        name?: string;
        phone?: string;
        status?: string;
      };
    }) => userManagementService.updateUser(userId, payload),
    onSuccess: (data) => {
      console.log('✅ User updated successfully:', data);
      console.log('✅ Updated warehouse:', data.warehouse);
      showToast('User updated successfully', 'success');
      setEditingUser(null);
      clearEditErrors();
      
      // Force a complete refresh of all user data
      queryClient.invalidateQueries({ queryKey: ['user-management'] });
      queryClient.removeQueries({ queryKey: ['user-management'] });
      
      // Refetch immediately
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ['user-management'] });
      }, 100);
    },
    onError: (error: ApiError) => {
      console.error('❌ Update user error:', error);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach(err => showToast(err, 'error'));
      } else {
        showToast(error.message || 'Failed to update user', 'error');
      }
    },
  });

  const openEditModal = (user: ManagedUser) => {
    setEditingUser(user);
    setEditForm({
      warehouseId: user.warehouseId ?? undefined,
      roleId: user.roleId,
      name: user.name ?? '',
      phone: user.phone ?? '',
      status: (user.status as UserStatus) ?? 'active',
    });
    clearEditErrors(); // Clear any previous errors
  };

  const handleCreateSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    // Validate form before submission (Client Onboarding pattern)
    if (!validateCreateForm()) {
      return;
    }
    
    createUserMutation.mutate({
      email: createForm.email.trim(),
      warehouseId: createForm.warehouseId,
      roleId: createForm.roleId,
      name: createForm.name.trim(),
      phone: createForm.phone.trim(),
      status: createForm.status || 'active',
    });
  };

  const handleEditSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    console.log('🔴 handleEditSubmit called');
    
    if (!editingUser) {
      console.log('🔴 No editing user, returning');
      return;
    }

    console.log('🔴 Editing user:', editingUser);
    console.log('🔴 Edit form:', editForm);

    // Validate form before submission (Client Onboarding pattern)
    if (!validateEditForm()) {
      console.log('🔴 Validation failed');
      return;
    }

    const payload = {
      warehouseId: editForm.warehouseId,
      roleId: editForm.roleId,
      name: editForm.name.trim(),
      phone: editForm.phone.trim(),
      status: editForm.status,
    };

    console.log('🔍 Edit form payload:', payload);
    console.log('🔍 Editing user ID:', editingUser.id);
    
    const confirmed = window.confirm(
      `The details for ${editingUser.name || editingUser.email} are being updated. Click OK to save your changes, or Cancel to discard them.`,
    );

    if (!confirmed) {
      return;
    }

    updateUserMutation.mutate({
      userId: editingUser.id,
      payload,
    });
  };

  const clearFilters = () => {
    setSort('createdAt');
    setOrder('DESC');
    setRoleFilter(undefined);
  };

  // Error clearing functions (Client Onboarding pattern)
  const clearCreateErrors = () => {
    setCreateErrors({});
  };

  const clearEditErrors = () => {
    setEditErrors({});
  };

  return (
    <Layout>
      <div className="min-h-screen px-1 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100 scrollbar-hide" style={{ overflowY: 'auto' }}>
        {/* Header with enhanced shadow */}
        <div className="mb-4 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="flex items-center space-x-3 bg-gradient-to-r from-brand-100 to-brand-50 p-5 rounded-xl shadow-lg border-2 border-brand-300 hover:shadow-xl transition-shadow duration-300">
            <div className="p-3 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl shadow-md transform transition-transform duration-300 hover:scale-110 hover:rotate-3">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">User Management</h2>
              <p className="text-gray-600 mt-1">Create tenant users, assign roles, and manage account status</p>
            </div>
          </div>
        </div>

        {/* User Table Card with enhanced shadows and borders */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:border-primary-300">
          {/* Header with Search and Actions in One Row */}
          <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Left: Title */}
              <div className="flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  User List
                  {(search || roleFilter) && (
                    <span className="ml-2 px-3 py-1 text-xs font-semibold bg-green-100 text-green-700 rounded-full flex items-center shadow-sm animate-pulse">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                      </svg>
                      Filtered
                    </span>
                  )}
                </h3>
              </div>

              {/* Center: Search Bar */}
              <div className="flex-1 min-w-[200px] max-w-md">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={draftSearch}
                    onChange={(e) => setDraftSearch(e.target.value)}
                    placeholder="Search by name, email, or phone..."
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    className="block w-full pl-10 pr-10 py-2.5 border-2 border-gray-300 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 hover:border-gray-400"
                  />
                  {draftSearch && (
                    <button
                      onClick={() => {
                        setDraftSearch('');
                        setSearch('');
                      }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                      title="Clear search"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Right: Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
                <button 
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-2.5 text-sm font-medium border rounded-xl flex items-center space-x-2 transition-all duration-200 shadow-sm ${
                    showFilters 
                      ? 'bg-gradient-to-r from-brand-500 to-brand-600 border-brand-600 text-white shadow-brand-200' 
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-md'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                  </svg>
                  <span>Filter & Sort</span>
                </button>
                
                <button 
                  onClick={() => {
                    setCreateForm({
                      ...INITIAL_CREATE_FORM,
                      warehouseId: user?.activeWarehouseId ?? 0,
                    });
                    clearCreateErrors();
                    setShowCreateModal(true);
                  }}
                  className="btn-primary transform transition-all duration-300 hover:scale-105 hover:shadow-lg"
                >
                  Add User
                </button>
              </div>
            </div>
          </div>

          {/* Filter & Sort Options Panel */}
          {showFilters && (
            <div className="bg-gradient-to-br from-brand-50 to-white p-5 mx-4 mb-4 rounded-xl border-2 border-brand-200 shadow-lg animate-in slide-in-from-top duration-300">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-primary-600 rounded-lg mr-3 shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                  </svg>
                </div>
                <h4 className="text-base font-semibold text-gray-900">Filter & Sort Options</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* Role Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1.5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Filter by Role
                  </label>
                  <ResponsiveSelect
                    options={roleOptions}
                    value={roleFilter ?? ''}
                    onChange={(val) => setRoleFilter(val ? Number(val) : undefined)}
                    placeholder="All Roles"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                  />
                </div>

                {/* Sort By */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1.5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    </svg>
                    Sort By
                  </label>
                  <ResponsiveSelect
                    options={[
                      { value: 'createdAt', label: 'Created Date' },
                      { value: 'name', label: 'Name' },
                      { value: 'email', label: 'Email' },
                      { value: 'status', label: 'Status' },
                    ]}
                    value={sort}
                    onChange={(val) => setSort(val as any)}
                    includeEmpty={false}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                  />
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1.5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                    Order
                  </label>
                  <ResponsiveSelect
                    options={[
                      { value: 'ASC', label: 'Ascending (A-Z, Old-New)' },
                      { value: 'DESC', label: 'Descending (Z-A, New-Old)' },
                    ]}
                    value={order}
                    onChange={(val) => setOrder(val as 'ASC' | 'DESC')}
                    includeEmpty={false}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white shadow-sm transition-all duration-200 hover:border-gray-400"
                  />
                </div>
              </div>

              {/* Filter Actions */}
              <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={clearFilters}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Reset Filters</span>
                </button>
              </div>
            </div>
          )}

          {/* Modern Table Design */}
          <div className="overflow-hidden">
            {/* Table Header - desktop only; mobile rows carry their own labels */}
            <div className="hidden lg:block bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
              <div className="grid grid-cols-12 gap-6 px-8 py-4 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                <div className="col-span-3">User</div>
                <div className="col-span-2">Role</div>
                <div className="col-span-2">Warehouse</div>
                <div className="col-span-2">Tenant</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2 text-center">Actions</div>
              </div>
            </div>

            {/* Table Body */}
            <div
              className="lg:divide-y lg:divide-gray-100 scrollbar-hide"
              style={{ maxHeight: '600px', overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onScroll={(e) => {
                const target = e.currentTarget;
                const scrollPercentage = (target.scrollTop / (target.scrollHeight - target.clientHeight)) * 100;
                
                // Load more when user scrolls to 90% of the content
                if (scrollPercentage > 90 && hasNextPage && !isFetchingNextPage) {
                  fetchNextPage();
                }
              }}
            >
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-16 px-6">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-200 border-t-blue-600 mb-4"></div>
                  <p className="text-gray-600 font-medium">Loading users...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No users found</h3>
                  <p className="text-gray-500 text-sm">
                    {search || roleFilter ? 'Try adjusting your filters' : 'Add your first user to get started.'}
                  </p>
                </div>
              ) : (
                users.map((user) => (
                  <div
                    key={user.id}
                    className="mx-2 my-2.5 p-3 rounded-xl border border-gray-200 shadow-sm bg-white lg:mx-0 lg:my-0 lg:p-0 lg:rounded-none lg:border-0 lg:shadow-none lg:bg-transparent lg:grid lg:grid-cols-12 lg:gap-6 lg:px-8 lg:py-5 hover:bg-gradient-to-r hover:from-brand-50 hover:to-transparent transition-all duration-300 lg:border-b lg:border-gray-200 lg:last:border-b-0 group hover:shadow-md lg:hover:scale-[1.01] lg:transform"
                  >
                    {/* User - doubles as the card header on mobile */}
                    <div className="lg:col-span-3 flex items-center pb-2 mb-2 border-b border-gray-100 lg:pb-0 lg:mb-0 lg:border-0">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors duration-200" title={user.name || user.email}>
                          {user.name || 'Unnamed User'}
                        </p>
                        <p className="text-xs text-gray-600 truncate">{user.email}</p>
                        <p className="text-xs text-gray-500 truncate">{user.phone || 'No phone'}</p>
                      </div>
                    </div>

                    {/* Role */}
                    <div className="lg:col-span-2 flex items-center justify-between gap-2 mb-1 lg:mb-0 lg:justify-start">
                      <span className="text-[11px] text-gray-500 lg:hidden">Role</span>
                      <p className="text-sm text-gray-700 truncate" title={user.role?.name}>
                        {user.role?.name || 'No role'}
                      </p>
                    </div>

                    {/* Warehouse */}
                    <div className="lg:col-span-2 flex items-center justify-between gap-2 mb-1 lg:mb-0 lg:justify-start">
                      <span className="text-[11px] text-gray-500 lg:hidden">Warehouse</span>
                      <p className="text-sm text-gray-700 truncate" title={user.warehouse?.name}>
                        {user.warehouse?.name || '-'}
                      </p>
                    </div>

                    {/* Tenant */}
                    <div className="lg:col-span-2 flex items-center justify-between gap-2 mb-1 lg:mb-0 lg:justify-start">
                      <span className="text-[11px] text-gray-500 lg:hidden">Tenant</span>
                      <p className="text-sm text-gray-700 truncate" title={user.tenant?.name}>
                        {user.tenant?.name || '-'}
                      </p>
                    </div>

                    {/* Status */}
                    <div className="lg:col-span-1 flex items-center justify-between gap-2 lg:justify-start">
                      <span className="text-[11px] text-gray-500 lg:hidden">Status</span>
                      <span className={`px-3 py-1.5 inline-flex text-xs font-semibold rounded-full whitespace-nowrap shadow-sm group-hover:shadow-md transition-shadow duration-200
                        ${user.status === 'active' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}
                      >
                        {user.status?.charAt(0).toUpperCase() + user.status?.slice(1)}
                      </span>
                    </div>

                    {/* Actions - full-width footer button on mobile, icon on desktop */}
                    <div className="lg:col-span-2 flex items-center justify-center gap-2 pt-2 mt-2 border-t border-gray-100 lg:pt-0 lg:mt-0 lg:border-0">
                      <button
                        onClick={() => openEditModal(user)}
                        className="group relative p-2 w-full lg:w-auto min-h-[42px] lg:min-h-0 flex items-center justify-center rounded-lg bg-green-50 text-green-600 hover:bg-green-600 hover:text-white transition-all duration-200 lg:hover:scale-110 hover:shadow-lg active:scale-[0.98]"
                        title="Edit User"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span className="lg:hidden ml-1.5 text-xs font-semibold">Edit</span>
                        <span className="hidden lg:block absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                          Edit
                        </span>
                      </button>
                    </div>
                  </div>
                ))
              )}
              
              {/* Loading more indicator */}
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-6 px-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-3 border-brand-200 border-t-blue-600"></div>
                  <p className="ml-3 text-gray-600 font-medium">Loading more users...</p>
                </div>
              )}
              
              {/* Show total count */}
              {!isLoading && users.length > 0 && (
                <div className="py-4 px-8 bg-gray-50 border-t border-gray-200 text-center">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-semibold text-gray-900">{users.length}</span> of <span className="font-semibold text-gray-900">{totalUsers}</span> users
                    {hasNextPage && <span className="ml-2 text-brand-600">(scroll for more)</span>}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end lg:items-center justify-center z-50 p-3 lg:p-4">
          <div className="w-full max-w-xl rounded-2xl lg:rounded-2xl bg-white shadow-xl max-h-[85vh] overflow-y-auto animate-slide-up lg:animate-none">
            <form onSubmit={handleCreateSubmit} className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Create User</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    clearCreateErrors();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="bg-brand-50 border border-brand-200 rounded-lg p-3">
                <p className="text-sm text-brand-800">
                  <strong>Note:</strong> The user will receive a welcome email with a link to set up their password.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Input
                    label="Full Name"
                    required
                    value={createForm.name ?? ''}
                    onChange={handleAlphabetsOnly(handleCreateInputChange, 'name')}
                    error={createErrors.name}
                    placeholder="Enter full name (letters only)"
                  />
                </div>
                <div>
                  <Input
                    label="Phone"
                    required
                    value={createForm.phone ?? ''}
                    onChange={handleMobileChange(handleCreateInputChange, 'phone')}
                    error={createErrors.phone}
                    placeholder="Enter 10-digit mobile number"
                    maxLength={10}
                  />
                </div>
                <div>
                  <Input
                    label="Email"
                    required
                    value={createForm.email}
                    onChange={handleEmailChange(handleCreateInputChange, 'email')}
                    error={createErrors.email}
                    placeholder="Enter valid email address"
                    type="email"
                  />
                </div>
                <div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                    <ResponsiveSelect
                      options={roleOptions}
                      value={createForm.roleId ?? ''}
                      onChange={(val) => {
                        handleCreateInputChange('roleId', val ? Number(val) : undefined);
                      }}
                      placeholder="Select role"
                      className={`w-full px-3 py-2 border rounded-lg transition-colors duration-200 ${
                        createErrors.roleId
                          ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                      }`}
                    />
                    {createErrors.roleId && (
                      <p className="mt-1 text-sm text-red-600">{createErrors.roleId}</p>
                    )}
                  </div>
                </div>
                <div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse *</label>
                    <ResponsiveSelect
                      options={warehouses.map((warehouse: any) => ({ value: warehouse.id, label: warehouse.name }))}
                      value={createForm.warehouseId || user?.activeWarehouseId || ''}
                      onChange={(val) => {
                        handleCreateInputChange('warehouseId', Number(val));
                      }}
                      placeholder="Select warehouse"
                      className={`w-full px-3 py-2 border rounded-lg transition-colors duration-200 ${
                        createErrors.warehouseId
                          ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                      }`}
                    />
                    {createErrors.warehouseId && (
                      <p className="mt-1 text-sm text-red-600">{createErrors.warehouseId}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <ResponsiveSelect
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                    value={createForm.status ?? 'active'}
                    onChange={(val) =>
                      setCreateForm((current) => ({
                        ...current,
                        status: val,
                      }))
                    }
                    includeEmpty={false}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    clearCreateErrors();
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createUserMutation.isPending}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {createUserMutation.isPending ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/40 flex items-end lg:items-center justify-center z-50 p-3 lg:p-4">
          <div className="w-full max-w-xl rounded-2xl lg:rounded-2xl bg-white shadow-xl max-h-[85vh] overflow-y-auto animate-slide-up lg:animate-none">
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Edit User</h2>
                  <p className="text-sm text-gray-500 mt-1">{editingUser.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEditingUser(null);
                    clearEditErrors();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Input
                    label="Full Name"
                    required
                    value={editForm.name}
                    onChange={handleAlphabetsOnly(handleEditInputChange, 'name')}
                    error={editErrors.name}
                    placeholder="Enter full name (letters only)"
                  />
                </div>
                <div>
                  <Input
                    label="Phone"
                    required
                    value={editForm.phone}
                    onChange={handleMobileChange(handleEditInputChange, 'phone')}
                    error={editErrors.phone}
                    placeholder="Enter 10-digit mobile number"
                    maxLength={10}
                  />
                </div>
                <div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                    <ResponsiveSelect
                      options={roleOptions}
                      value={editForm.roleId ?? ''}
                      onChange={(val) => {
                        handleEditInputChange('roleId', val ? Number(val) : undefined);
                      }}
                      placeholder="Select role"
                      className={`w-full px-3 py-2 border rounded-lg transition-colors duration-200 ${
                        editErrors.roleId
                          ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                      }`}
                    />
                    {editErrors.roleId && (
                      <p className="mt-1 text-sm text-red-600">{editErrors.roleId}</p>
                    )}
                  </div>
                </div>
                <div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse *</label>
                    <ResponsiveSelect
                      options={warehouses.map((warehouse: any) => ({ value: warehouse.id, label: warehouse.name }))}
                      value={editForm.warehouseId ?? ''}
                      onChange={(val) => {
                        handleEditInputChange('warehouseId', val ? Number(val) : undefined);
                      }}
                      placeholder="Select warehouse"
                      className={`w-full px-3 py-2 border rounded-lg transition-colors duration-200 ${
                        editErrors.warehouseId
                          ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                      }`}
                    />
                    {editErrors.warehouseId && (
                      <p className="mt-1 text-sm text-red-600">{editErrors.warehouseId}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <ResponsiveSelect
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                    value={editForm.status}
                    onChange={(val) =>
                      setEditForm((current) => ({
                        ...current,
                        status: val as UserStatus,
                      }))
                    }
                    includeEmpty={false}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditingUser(null);
                    clearEditErrors();
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateUserMutation.isPending}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
